<template>
    <OutOfPageAdContainer
        :adInfo="adInfo"
        :isAdFreeActive="adDisabledStore.isAdFreeActive"
        :isAdFreeButtonShown="false"
    >
        <video
            ref="videoElem"
            :class="$style.video"
            playsinline
        />
    </OutOfPageAdContainer>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { onUnmounted, ref, watch } from 'vue'

import { AdLoader } from '@/modules/adv/ad-loader'
import { useAdDisabledStore } from '@/store/ad-disabled-store'
import { useAppStore } from '@/store/app-store'
import { random, timer } from '@/utils/helpers'

import type { AdInfo } from '../../types'
import { LOADER_FILLER_DURATION_MS } from '../../utils'
import OutOfPageAdContainer from '../OutOfPageAdContainer/OutOfPageAdContainer.vue'

const videos = [
    {
        vertical: 'https://static.playgama.com/p-video/ad-free/bear_9x16_480x854.mp4',
        horizontal: 'https://static.playgama.com/p-video/ad-free/bear_16x9_960_540.mp4',
    },
    {
        vertical: 'https://static.playgama.com/p-video/ad-free/sport_480x854.mp4',
        horizontal: 'https://static.playgama.com/p-video/ad-free/sport_16x9_960_540.mp4',
    },
]

const emit = defineEmits<{
    stop: []
}>()

const props = defineProps<{
    adLoader?: AdLoader
    debranding?: boolean
}>()

const { platform } = storeToRefs(useAppStore())
const adDisabledStore = useAdDisabledStore()

const adInfo = ref<AdInfo>({
    isVisible: false,
    secondsLeft: 0,
    placement: 'rewarded',
    debranding: props.debranding,
})
const videoElem = ref<HTMLVideoElement>()

function loadVideo(videoEl: HTMLVideoElement): void {
    const video = videos[Math.floor(random() * videos.length)]
    const isHorizontal = window.innerHeight < window.innerWidth
    videoEl.src = platform.value === 'desktop' || isHorizontal ? video!.horizontal : video!.vertical
}

async function startVideo(videoEl: HTMLVideoElement) {
    props.adLoader?.show('rewarded')
    loadVideo(videoEl)
    if (props.adLoader) {
        await timer(LOADER_FILLER_DURATION_MS)
    }

    try {
        await videoEl.play()
    } catch {
        await props.adLoader?.destroy()
        emit('stop')
        return
    }

    videoEl.addEventListener('timeupdate', () => {
        adInfo.value.secondsLeft = Math.round(videoEl.duration - videoEl.currentTime)
    })
    videoEl.addEventListener('ended', () => emit('stop'), { once: true })

    adInfo.value.isVisible = true
    await props.adLoader?.destroy()
}

watch(videoElem, (videoEl) => videoEl && startVideo(videoEl), { once: true })

onUnmounted(() => {
    props.adLoader?.destroy()
})
</script>

<style module>
.video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-color: rgba(0, 0, 0, 0.9);
}
</style>
